import { Injectable } from '@angular/core';
import { SgIsoStore } from '../iso-store';

@Injectable({
  providedIn: 'root',
})
export class SgCountryService {
  constructor(private isoStore: SgIsoStore) {}

  /**
   * Get country name synchronously. Returns null if store not ready or country not found.
   * Use this when you need immediate results and can handle null values.
   */
  getCountryName(countryCode: string): string | null {
    if (!this.isoStore.ready) {
      return null;
    }

    const country = this.isoStore.query(countryCode);
    return country?.name || null;
  }

  /**
   * Get country flag synchronously. Returns null if store not ready or country not found.
   */
  getCountryFlag(countryCode: string): string | null {
    if (!this.isoStore.ready) {
      return null;
    }

    const country = this.isoStore.query(countryCode);
    return country?.flag || null;
  }

  /**
   * Get country name asynchronously. Waits for store to be ready.
   * Use this when you need guaranteed results and can work with promises.
   */
  async getCountryNameAsync(countryCode: string): Promise<string | null> {
    await this.isoStore.onReady;
    return this.getCountryName(countryCode);
  }

  /**
   * Get country flag asynchronously. Waits for store to be ready.
   */
  async getCountryFlagAsync(countryCode: string): Promise<string | null> {
    await this.isoStore.onReady;
    return this.getCountryFlag(countryCode);
  }

  /**
   * Check if the ISO store is ready
   */
  isReady(): boolean {
    return this.isoStore.ready;
  }

  /**
   * Get a promise that resolves when the store is ready
   */
  onReady(): Promise<boolean> {
    return this.isoStore.onReady;
  }

  /**
   * Utility method for components that need country names with fallbacks
   */
  getCountryNameWithFallback(countryCode: string, fallback: string = countryCode): string {
    return this.getCountryName(countryCode) || fallback;
  }
}
